import React, { useEffect, useCallback, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { transformStrToUnderscore } from 'utils/router'
import {
  useCreateCommodityMutation,
  useGetCommoditiesQuery,
  useGetCommodityQuery,
  usePutCommodityMutation,
} from 'services/commodities'
import { setSnackBar } from 'state/ui'
import { formOptions } from 'utils/validation/commodity'
import AlertSnackbar from 'components/generic/snackbars'
import Loading from 'components/generic/Loading'
import WizardStepper from 'components/generic/stepper/WizardStepper'
import GeneralPropertiesForm from 'components/containers/commodities/new-commodity/components/GeneralPropertiesForm'
import CommodityConfirmationForm from 'components/containers/commodities/new-commodity/components/CommodityConfirmationForm'
import CommodityComplianceForm from 'components/containers/commodities/new-commodity/components/CommodityComplianceForm'
import { MODAL_VIEW_MODES, COMMODITY_RULE_TYPES, COMMODITY_TYPE, COMMODITY_CLASS } from 'utils/constants'

const NewCommodityContainer = ({ onFormStateChange, onHideModal }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const methods = useForm({
    ...formOptions,
    mode: 'onTouched',
    defaultValues: {
      general: {
        name: '',
        code: '',
        type: '',
        class: '',
      },
      weightThreshold: {
        enabled: false,
        amount: 0,
      },
    },
  })
  const { handleSubmit, formState, reset, getValues, watch, trigger } = methods
  const { dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length

  const [title, setTitle] = useState('Add New Commodity')
  const { viewMode, commodityId } = useSelector(state => state.ui.formModalProps)
  const isUpdate = viewMode === MODAL_VIEW_MODES.EDIT

  const { enableCommodityWeightThresholds } = useSelector(state => state.featureFlags?.variables || {})

  const [createCommodity, { isLoading: isCreatingCommodity }] = useCreateCommodityMutation()
  const [updateCommodity, { isLoading: isUpdatingCommodity }] = usePutCommodityMutation()
  const [showFinishedForm, setShowFinishedForm] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [commodityNameDuplicated, setCommodityNameDuplicated] = useState(false)
  const [commodityCodeDuplicated, setCommodityCodeDuplicated] = useState(false)
  const currentCommodityName = watch('general.name')
  const currentCommodityCode = watch('general.code')

  const { data: commoditiesNameMatch } = useGetCommoditiesQuery(
    { filter: JSON.stringify({ search: currentCommodityName }), pageSize: 50 },
    { skip: !currentCommodityName },
  )

  const { data: commoditiesCodeMatch } = useGetCommoditiesQuery(
    { filter: JSON.stringify({ search: currentCommodityCode }), pageSize: 50 },
    { skip: !currentCommodityCode },
  )

  const { data: commodities, isLoading: isLoadingCommodities } = useGetCommoditiesQuery({
    pageSize: 1000,
  })

  const distinctCommodityTypes = useMemo(
    () => [
      ...new Set(
        commodities?.Items?.map(item => item.type).filter(
          type =>
            type &&
            type !== '' &&
            type !== COMMODITY_CLASS.FERROUS &&
            type !== COMMODITY_CLASS.NON_FERROUS &&
            type !== 'WASTE',
        ),
      ),
    ],
    [commodities?.Items],
  )

  const { data: commodityData } = useGetCommodityQuery({ commodityId }, { skip: !commodityId })

  useEffect(() => {
    if (commodityData) {
      setTitle(commodityData.name)
      const weightThresholdRule = commodityData.rules?.find(rule => rule.type === COMMODITY_RULE_TYPES.WEIGHT_THRESHOLD)
      reset({
        general: {
          name: commodityData.name,
          code: commodityData.code,
          type: commodityData.type,
          class: commodityData.class,
        },
        weightThreshold: {
          enabled: weightThresholdRule?.enabled || false,
          amount: weightThresholdRule?.parameters?.amount || 0,
        },
      })
    }
  }, [commodityData, reset])

  useEffect(() => {
    const duplicate = commoditiesNameMatch?.Items?.find(
      commodity => commodity?.name?.toLowerCase() === currentCommodityName?.toLowerCase(),
    )
    setCommodityNameDuplicated(!!duplicate && duplicate?.id !== commodityData?.id)
  }, [commodityData, commoditiesNameMatch, currentCommodityName])

  useEffect(() => {
    const duplicate = commoditiesCodeMatch?.Items?.find(
      commodity => commodity?.code?.toLowerCase() === currentCommodityCode?.toLowerCase(),
    )
    setCommodityCodeDuplicated(!!duplicate && duplicate?.id !== commodityData?.id)
  }, [commodityData, commoditiesCodeMatch, currentCommodityCode])

  useEffect(() => onFormStateChange(false), [])

  const steps = [
    {
      label: `${isUpdate ? 'Commodity' : 'General'} Properties`,
      showStepper: true,
      stepOptional: false,
      footerButtonTitles: [
        isUpdate ? 'SAVE' : enableCommodityWeightThresholds ? 'NEXT: COMPLIANCE' : 'FINISH',
        'CANCEL',
      ],
      contentComponent: (
        <GeneralPropertiesForm
          commodityData={commodityData}
          distinctCommodityTypes={distinctCommodityTypes}
          commodityNameDuplicated={commodityNameDuplicated}
          commodityCodeDuplicated={commodityCodeDuplicated}
        />
      ),
    },
    ...(enableCommodityWeightThresholds
      ? [
          {
            label: 'Compliance',
            showStepper: true,
            stepOptional: false,
            footerButtonTitles: ['FINISH', 'BACK'],
            contentComponent: <CommodityComplianceForm />,
          },
        ]
      : []),
  ]

  const handleError = error => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.commoditiesData?.error || 'There was an error creating new commodity',
      }),
    )
  }

  const handleSuccess = () =>
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: `Commodity successfully ${isUpdate ? 'updated' : 'created'}`,
      }),
    )

  const formatCommodityData = ({ general, weightThreshold }) => ({
    code: general?.code?.toUpperCase(),
    date: Date.now(),
    name: general?.name,
    type: general?.type,
    class: general?.class,
    urlName: general?.name?.toLowerCase(),
    user: general?.user,
    rules: [
      {
        type: COMMODITY_RULE_TYPES.WEIGHT_THRESHOLD,
        enabled: weightThreshold?.enabled || false,
        parameters: {
          amount: weightThreshold?.amount || 0,
        },
      },
    ],
  })

  const validateFields = async step => {
    let fields = []
    switch (step) {
      case 0:
        fields = ['general']
        break
      case 1:
        fields = ['weightThreshold']
        break
      default:
        break
    }
    const validationResult = await trigger(fields)

    return validationResult && !commodityNameDuplicated && !commodityCodeDuplicated
  }

  const onValidatePartial = async step => {
    if (isUpdate) {
      return !isDirty
    }
    return validateFields(step)
  }

  const onSubmitPartial = async step => {
    const validation = await validateFields(step)
    if (!validation) return

    const submittedData = getValues()

    const commodity = {
      ...formatCommodityData(submittedData),
      id: commodityId,
      archive: false,
    }
    await updateCommodity({ commodity })
      .unwrap()
      .then(() => {
        handleSuccess()
        onHideModal()
      })
      .catch(error => handleError(error))
  }

  const onSubmit = async submittedData => {
    if (!validateFields()) return
    const commodity = formatCommodityData(submittedData)
    await createCommodity({ commodity })
      .unwrap()
      .then(async payload => {
        handleSuccess(payload)
        setShowFinishedForm(true)
      })
      .catch(error => handleError(error))
  }

  const onViewCommodity = () => {
    onHideModal()
    navigate(transformStrToUnderscore(getValues('general.name').toLowerCase()))
  }

  const onAddAnotherCommodity = useCallback(() => {
    reset({
      general: {
        name: '',
        code: '',
        type: COMMODITY_TYPE[0].id,
        class: COMMODITY_TYPE[0].id,
      },
      weightThreshold: {
        enabled: false,
        amount: 0,
      },
    })
    setCommodityNameDuplicated(false)
    setCommodityCodeDuplicated(false)
    setShowFinishedForm(false)
  }, [])

  return (
    <FormProvider {...methods}>
      <AlertSnackbar
        message={errorMessage}
        isOpen={!!errorMessage}
        severity="error"
        handleClose={() => setErrorMessage('')}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        {isCreatingCommodity || (isUpdatingCommodity && <Loading />)}
        {showFinishedForm ? (
          <CommodityConfirmationForm
            onViewCommodity={onViewCommodity}
            onAddAnotherCommodity={onAddAnotherCommodity}
            onHideModal={onHideModal}
          />
        ) : (
          <WizardStepper
            steps={steps}
            onHideModal={onHideModal}
            title={title}
            validationCheck
            nonLinear={isUpdate}
            disableAlertModal={!isDirty}
            onSubmitPartial={onSubmitPartial}
            onValidatePartial={onValidatePartial}
          />
        )}
      </form>
    </FormProvider>
  )
}

NewCommodityContainer.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

NewCommodityContainer.defaultProps = {
  onFormStateChange: noop,
  onHideModal: noop,
}

export default NewCommodityContainer
